/**
 * Invite acceptance — DESIGN.md §9.1.
 *
 * The landing page for the link in the invite email: set a password, and the
 * server signs you straight in.
 */
import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { passwordSchema } from '@slate/shared';
import { ApiError } from '@/lib/api';
import { useAcceptInvite } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';

export const Route = createFileRoute('/invite')({
  validateSearch: z.object({ token: z.string().optional() }),
  component: InvitePage,
});

function InvitePage(): JSX.Element {
  const { token } = Route.useSearch();
  const accept = useAcceptInvite();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  // Validate against the same schema the API enforces, so the requirement is
  // stated once (§18.4 clause 2) and shown before a round trip.
  const localError =
    password.length > 0 && !passwordSchema.safeParse(password).success
      ? (passwordSchema.safeParse(password).error?.issues[0]?.message ?? 'Choose a longer password')
      : confirm.length > 0 && confirm !== password
        ? 'Those passwords do not match'
        : null;

  const serverError = accept.error instanceof ApiError ? accept.error.message : null;

  if (!token) {
    return (
      <Centered>
        <h1 className="text-lg font-semibold">That invite link is incomplete</h1>
        <p className="text-muted-foreground mt-2 text-sm">
          Open the link from your invite email again, or ask an administrator to re-send it.
        </p>
      </Centered>
    );
  }

  return (
    <Centered>
      <h1 className="text-lg font-semibold tracking-tight">Set your password</h1>
      <p className="text-muted-foreground mt-1 text-sm">Choose a password and you’re into Slate.</p>

      {serverError ? (
        <p
          role="alert"
          className="border-destructive/30 bg-destructive/10 text-destructive mt-4 rounded-md border px-3 py-2 text-sm"
        >
          {serverError}
        </p>
      ) : null}

      <form
        className="mt-4 space-y-3 text-left"
        onSubmit={(event) => {
          event.preventDefault();
          if (localError) return;
          // AuthGate redirects once the session exists (see __root.tsx).
          accept.mutate({ token, password });
        }}
      >
        <div className="space-y-1.5">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            aria-invalid={Boolean(localError)}
            aria-describedby="password-hint"
          />
          <p id="password-hint" className="text-muted-foreground text-xs">
            At least 12 characters.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="confirm">Confirm password</Label>
          <Input
            id="confirm"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            aria-invalid={Boolean(localError)}
          />
        </div>
        {localError ? <p className="text-destructive text-sm">{localError}</p> : null}
        <Button
          type="submit"
          className="w-full"
          disabled={accept.isPending || Boolean(localError) || password.length === 0}
        >
          {accept.isPending ? <Spinner /> : null}
          Set password and sign in
        </Button>
      </form>
    </Centered>
  );
}

function Centered({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="bg-muted/40 grid min-h-dvh place-items-center p-4">
      <div className="bg-card w-full max-w-sm rounded-xl border p-6 text-center shadow-sm">
        {children}
      </div>
    </div>
  );
}
